'use client'

import { useEffect, useRef, useState } from 'react'
import { WORKIZ_CHANNELS, WORKIZ_STAGES, type WorkizFeature } from '@/lib/workiz-reporting-data'
import { CardCheck } from '@/components/zoho/icons'

function FeatureLine({ label, detail }: WorkizFeature) {
  return (
    <li className="flex items-start gap-2.5">
      <CardCheck />
      <span className="min-w-0 text-[15px] leading-[1.45] text-slate-700">
        <span className="font-semibold text-[#0a2b5c]">{label}</span>
        {detail ? <span className="text-slate-500"> - {detail}</span> : null}
      </span>
    </li>
  )
}

const STAGE_COLORS = [
  ['from-blue-600', 'to-indigo-700'],
  ['from-cyan-500', 'to-blue-600'],
  ['from-emerald-500', 'to-teal-700'],
] as const

function ChannelGrid() {
  return (
    <div className="mt-6 grid gap-x-5 gap-y-4 sm:grid-cols-2">
      {WORKIZ_CHANNELS.map((cat) => (
        <div key={cat.category}>
          <h3 className="text-[10px] font-extrabold uppercase tracking-[0.13em] text-slate-400">
            {cat.category}
          </h3>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {cat.tools.map((tool) => (
              <span
                key={tool.name}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600 shadow-sm"
              >
                {tool.domain && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={`https://www.google.com/s2/favicons?domain=${tool.domain}&sz=32`}
                    alt=""
                    width={12}
                    height={12}
                    className="h-3 w-3"
                  />
                )}
                {tool.name}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function StageCard({
  stage,
  index,
}: {
  stage: (typeof WORKIZ_STAGES)[number]
  index: number
}) {
  const colors = STAGE_COLORS[index]

  return (
    <article className="relative mx-auto flex h-auto min-h-[560px] w-full max-w-5xl flex-col overflow-hidden rounded-[32px] border border-white/70 bg-white shadow-[0_34px_110px_rgba(15,35,70,0.26)] sm:h-[620px] sm:min-h-0 lg:h-[min(640px,calc(100dvh-130px))]">
      <div className={`relative shrink-0 overflow-hidden bg-gradient-to-r ${colors[0]} ${colors[1]} px-7 py-4 text-white sm:px-10 sm:py-5`}>
        <div className="absolute -right-8 -top-20 h-48 w-48 rounded-full border-[38px] border-white/10" />
        <div className="relative flex items-center justify-between gap-5">
          <h3 className="text-[30px] font-black leading-none tracking-[-0.04em] sm:text-[34px]">
            {stage.title}
          </h3>
          <span className="text-[12px] font-bold uppercase tracking-[0.12em] text-white/65">
            {index + 1} of {WORKIZ_STAGES.length}
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-visible p-7 sm:min-h-0 sm:overflow-y-auto sm:p-10 lg:px-12 lg:py-9">
        <p className="max-w-3xl text-[28px] font-semibold leading-[1.16] tracking-[-0.035em] text-[#0a2b5c] sm:text-[38px]">
          {stage.claim}
          <em className="font-normal italic">{stage.claimEm}</em>
        </p>

        <ul className="mt-7 grid gap-3 sm:grid-cols-2">
          {stage.items.map((item) => (
            <FeatureLine key={item.label} {...item} />
          ))}
        </ul>

        {stage.extras === 'channels' ? (
          <>
            <ChannelGrid />
            <p className="mt-5 border-t border-slate-100 pt-4 text-[13px] leading-[1.5] text-slate-500">
              {stage.practice}
            </p>
          </>
        ) : (
          <div className="mt-8 rounded-2xl border border-blue-100 bg-[#f4f8ff] p-5">
            <b className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#13479c]">
              In practice
            </b>
            <p className="mt-2 text-[14px] leading-[1.55] text-slate-600">{stage.practice}</p>
          </div>
        )}
      </div>

      <div className="flex shrink-0 justify-center border-t border-slate-100 bg-white px-6 py-4">
        <a
          href="#plans"
          className="inline-flex h-11 items-center justify-center rounded-full bg-[#13479c] px-7 text-[15px] font-bold text-white shadow-[0_10px_24px_rgba(19,71,156,0.24)] transition-colors hover:bg-[#0f3a7a]"
          onClick={(event) => {
            event.preventDefault()
            document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }}
        >
          Book Services
        </a>
      </div>
    </article>
  )
}

export default function WorkizStages() {
  const trackRef = useRef<HTMLDivElement>(null)
  const [progress, setProgress] = useState(0)
  const [canPin, setCanPin] = useState(false)

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1001px)')
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setCanPin(desktop.matches && !reduced.matches)
    update()
    desktop.addEventListener('change', update)
    reduced.addEventListener('change', update)
    return () => {
      desktop.removeEventListener('change', update)
      reduced.removeEventListener('change', update)
    }
  }, [])

  useEffect(() => {
    if (!canPin) return
    let raf = 0
    const paint = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        const el = trackRef.current
        if (!el) return
        const rect = el.getBoundingClientRect()
        const total = rect.height - window.innerHeight
        setProgress(total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 0)
      })
    }
    paint()
    window.addEventListener('scroll', paint, { passive: true })
    window.addEventListener('resize', paint)
    return () => {
      window.removeEventListener('scroll', paint)
      window.removeEventListener('resize', paint)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [canPin])

  const position = progress * (WORKIZ_STAGES.length - 1)
  const current = Math.round(position)

  const jumpTo = (index: number) => {
    const el = trackRef.current
    if (!el) return
    const total = el.offsetHeight - window.innerHeight
    const top = el.getBoundingClientRect().top + window.scrollY + total * (index / (WORKIZ_STAGES.length - 1))
    window.scrollTo({ top, behavior: 'smooth' })
  }

  return (
    <div
      ref={trackRef}
      className="relative bg-[radial-gradient(circle_at_top_left,#dce9ff_0,transparent_38%),radial-gradient(circle_at_bottom_right,#e6dcff_0,transparent_36%),#eef4fc]"
      style={canPin ? { height: `${WORKIZ_STAGES.length * 100}dvh` } : undefined}
    >
      <div className={canPin ? 'sticky top-0 h-dvh overflow-hidden' : 'space-y-8 px-4 py-12 sm:px-6'}>
        {canPin && (
          <>
            <div className="absolute left-1/2 top-5 z-[200] flex -translate-x-1/2 items-center gap-1.5 rounded-2xl border border-white/80 bg-white/85 p-2 shadow-xl backdrop-blur-xl">
              {WORKIZ_STAGES.map((stage, index) => (
                <button
                  key={stage.n}
                  type="button"
                  onClick={() => jumpTo(index)}
                  aria-label={`Go to ${stage.title}`}
                  className={`flex h-11 items-center rounded-xl px-4 text-[15px] font-extrabold tracking-[-0.02em] transition-all ${
                    index === current
                      ? 'bg-[#13479c] text-white shadow-md'
                      : 'text-slate-500 hover:bg-blue-50 hover:text-[#13479c]'
                  }`}
                >
                  {stage.title}
                </button>
              ))}
            </div>
            <div className="absolute left-0 right-0 top-0 z-20 h-1 bg-white/40">
              <div className="h-full bg-[#13479c]" style={{ width: `${progress * 100}%` }} />
            </div>
          </>
        )}

        {WORKIZ_STAGES.map((stage, index) => {
          const relative = index - position
          const isPast = relative < 0
          const opacity = canPin && relative <= -1 ? 0 : 1
          const transform = isPast
            ? `translate3d(${relative * 115}%, ${relative * 25}px, 0) rotate(${relative * 13}deg) scale(.97)`
            : `translate3d(${relative * 10}px, ${relative * 18}px, 0) rotate(${relative * 1.2}deg) scale(${1 - Math.min(relative, 5) * 0.025})`
          return (
            <div
              key={stage.n}
              className={canPin ? 'absolute inset-0 flex items-start px-8 pb-5 pt-36 lg:px-16' : ''}
              style={canPin ? {
                opacity,
                transform,
                zIndex: WORKIZ_STAGES.length - index,
                pointerEvents: Math.abs(relative) < 0.5 ? 'auto' : 'none',
              } : undefined}
              aria-hidden={canPin && opacity < 0.5}
            >
              <StageCard stage={stage} index={index} />
            </div>
          )
        })}
      </div>
    </div>
  )
}
